import { useState, useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { 
  Radio, 
  Play, 
  Pause, 
  Volume2, 
  Heart, 
  Search, 
  ExternalLink,
  Music,
  Globe,
  Headphones,
  Mic2,
  ListMusic,
  ChevronRight,
  Star,
  StarHalf,
  Clock
} from "lucide-react";
import { toast } from "sonner";

type RadioStation = {
  id: number;
  name: string;
  genre: string;
  frequency: string;
  location: string;
  image: string;
  isFavorite: boolean;
};

const RadioOnlinePage = () => {
  const [currentStation, setCurrentStation] = useState<RadioStation | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState([70]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredStations, setFilteredStations] = useState<RadioStation[]>([]);
  
  const radioStations: RadioStation[] = [
    {
      id: 1,
      name: "Rádio Nova Brasil FM",
      genre: "MPB / Pop",
      frequency: "89.7 FM",
      location: "São Paulo, Brasil",
      image: "https://placehold.co/200x200/3b82f6/FFFFFF?text=Nova+Brasil",
      isFavorite: true
    },
    {
      id: 2,
      name: "Antena 1",
      genre: "Adulto Contemporâneo",
      frequency: "94.7 FM",
      location: "Rio de Janeiro, Brasil",
      image: "https://placehold.co/200x200/10b981/FFFFFF?text=Antena+1",
      isFavorite: false
    },
    {
      id: 3,
      name: "Jovem Pan",
      genre: "Pop / Rock / Notícias",
      frequency: "100.9 FM",
      location: "São Paulo, Brasil",
      image: "https://placehold.co/200x200/f59e0b/FFFFFF?text=Jovem+Pan",
      isFavorite: true
    },
    {
      id: 4,
      name: "CBN",
      genre: "Notícias",
      frequency: "90.5 FM",
      location: "São Paulo, Brasil",
      image: "https://placehold.co/200x200/6366f1/FFFFFF?text=CBN",
      isFavorite: false
    },
    {
      id: 5,
      name: "Rádio Bandeirantes",
      genre: "Notícias / Esportes",
      frequency: "90.9 FM",
      location: "São Paulo, Brasil",
      image: "https://placehold.co/200x200/ec4899/FFFFFF?text=Bandeirantes",
      isFavorite: false
    },
    {
      id: 6,
      name: "Kiss FM",
      genre: "Rock Clássico",
      frequency: "92.5 FM",
      location: "São Paulo, Brasil",
      image: "https://placehold.co/200x200/8b5cf6/FFFFFF?text=Kiss+FM",
      isFavorite: true
    },
    {
      id: 7,
      name: "89 FM A Rádio Rock",
      genre: "Rock",
      frequency: "89.1 FM",
      location: "São Paulo, Brasil",
      image: "https://placehold.co/200x200/ef4444/FFFFFF?text=89+FM",
      isFavorite: false
    },
    {
      id: 8,
      name: "Rádio Eldorado",
      genre: "Notícias / Música Clássica",
      frequency: "107.3 FM",
      location: "São Paulo, Brasil",
      image: "https://placehold.co/200x200/14b8a6/FFFFFF?text=Eldorado",
      isFavorite: false
    }
  ];

  const categories = [
    { name: "Pop", count: 42 },
    { name: "Rock", count: 38 },
    { name: "Notícias", count: 15 },
    { name: "Clássica", count: 10 },
    { name: "Sertanejo", count: 25 },
    { name: "Jazz", count: 8 },
    { name: "Eletrônica", count: 20 },
    { name: "MPB", count: 30 },
    { name: "Gospel", count: 18 },
    { name: "Hip Hop", count: 12 }
  ];

  const currentlyPlaying = {
    title: "Oceano",
    artist: "Djavan",
    album: "Coisa de Acaso",
    duration: "4:15",
    startedAt: "13:45",
    coverImage: "https://placehold.co/300x300/3b82f6/FFFFFF?text=Album+Cover"
  };

  useEffect(() => {
    setFilteredStations(
      searchQuery 
        ? radioStations.filter(station => 
            station.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
            station.genre.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : radioStations
    );
  }, [searchQuery]);

  const handleStationSelect = (station: RadioStation) => {
    setCurrentStation(station);
    setIsPlaying(true);
    toast.success(`Sintonizado em ${station.name}`);
  };

  const handlePlayPause = () => {
    if (currentStation) {
      setIsPlaying(!isPlaying);
      toast.info(isPlaying ? `${currentStation.name} pausada` : `Reproduzindo ${currentStation.name}`);
    } else {
      toast.error("Selecione uma estação primeiro");
    }
  };

  const handleVolumeChange = (value: number[]) => {
    setVolume(value);
    toast.info(`Volume: ${value[0]}%`);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const toggleFavorite = (stationId: number) => {
    const updatedStations = radioStations.map(station => 
      station.id === stationId ? { ...station, isFavorite: !station.isFavorite } : station
    );
    
    // In a real app, we would update the state here
    toast.success(`Estação ${updatedStations.find(s => s.id === stationId)?.isFavorite ? 'adicionada aos' : 'removida dos'} favoritos`);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent py-2">
            Rádio Online
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Ouça as melhores estações de rádio de todo o Brasil e do mundo. Música, notícias e entretenimento 24 horas por dia.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          <div className="lg:col-span-2 space-y-8">
            {currentStation ? (
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle>Tocando Agora</CardTitle>
                  <CardDescription>
                    {currentStation.name} - {currentStation.frequency}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="md:w-1/3">
                      <img 
                        src={currentStation.image} 
                        alt={currentStation.name}
                        className="w-full aspect-square object-cover rounded-lg shadow-md"
                      />
                    </div>
                    <div className="md:w-2/3 space-y-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Estação</p>
                        <h3 className="text-xl font-bold">{currentStation.name}</h3>
                        <p className="text-muted-foreground">{currentStation.frequency} - {currentStation.location}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-muted-foreground">Gênero</p>
                        <p>{currentStation.genre}</p>
                      </div>
                      
                      <div className="pt-2">
                        <p className="text-sm text-muted-foreground mb-2">Tocando agora</p>
                        <div className="flex items-center gap-4">
                          <img 
                            src={currentlyPlaying.coverImage}
                            alt={currentlyPlaying.title}
                            className="w-16 h-16 rounded-md"
                          />
                          <div>
                            <h4 className="font-medium">{currentlyPlaying.title}</h4>
                            <p className="text-sm text-muted-foreground">{currentlyPlaying.artist} - {currentlyPlaying.album}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                              <Clock className="h-3 w-3" />
                              <span>{currentlyPlaying.startedAt} - {currentlyPlaying.duration}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 pt-4">
                        <Button 
                          variant="outline" 
                          size="icon" 
                          className="h-12 w-12 rounded-full"
                          onClick={handlePlayPause}
                        >
                          {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
                        </Button>
                        
                        <div className="flex-1 flex items-center gap-2">
                          <Volume2 className="h-5 w-5 text-muted-foreground" />
                          <Slider
                            value={volume}
                            onValueChange={handleVolumeChange}
                            max={100}
                            step={1}
                            className="flex-1"
                          />
                          <span className="text-xs text-muted-foreground w-8">{volume[0]}%</span>
                        </div>
                        
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => toggleFavorite(currentStation.id)}
                          className={currentStation.isFavorite ? "text-primary" : ""}
                        >
                          <Heart className="h-5 w-5" fill={currentStation.isFavorite ? "currentColor" : "none"} />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Selecione uma Estação</CardTitle>
                  <CardDescription>
                    Escolha uma estação de rádio para começar a ouvir
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Radio className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Nenhuma estação selecionada</p>
                  </div>
                </CardContent>
              </Card>
            )}
            
            <Card>
              <CardHeader>
                <CardTitle>Estações de Rádio</CardTitle>
                <CardDescription>
                  Encontre sua estação favorita
                </CardDescription>
                <form onSubmit={handleSearch} className="mt-4">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input 
                      type="search"
                      placeholder="Buscar estações..."
                      className="pl-9"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </form>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredStations.length > 0 ? (
                    filteredStations.map((station) => (
                      <div 
                        key={station.id}
                        className={`
                          flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors
                          ${currentStation?.id === station.id ? 'bg-muted' : ''}
                        `}
                        onClick={() => handleStationSelect(station)}
                      >
                        <img 
                          src={station.image}
                          alt={station.name}
                          className="w-12 h-12 rounded-md object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium truncate">{station.name}</h3>
                          <p className="text-xs text-muted-foreground truncate">{station.genre} • {station.frequency}</p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(station.id);
                          }}
                        >
                          <Heart 
                            className="h-4 w-4" 
                            fill={station.isFavorite ? "currentColor" : "none"}
                            color={station.isFavorite ? "var(--primary)" : undefined}
                          />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full text-center py-8">
                      <p className="text-muted-foreground">Nenhuma estação encontrada para "{searchQuery}"</p>
                    </div>
                  )}
                </div>
              </CardContent>
              <CardFooter className="justify-center border-t">
                <Button variant="link" className="text-primary">
                  Ver todas as estações <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>
          </div>
          
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Categorias</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {categories.map((category, index) => (
                  <Button 
                    key={index}
                    variant="ghost" 
                    className="w-full justify-between h-auto py-2"
                  >
                    <span>{category.name}</span>
                    <span className="bg-muted text-muted-foreground text-xs rounded-full px-2 py-0.5">
                      {category.count}
                    </span>
                  </Button>
                ))}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Mais Ouvidas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-2 rounded-full">
                    <Star className="h-4 w-4 text-primary" fill="currentColor" />
                  </div>
                  <div>
                    <p className="font-medium">Jovem Pan</p>
                    <p className="text-xs text-muted-foreground">Pop / Rock / Notícias</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-2 rounded-full">
                    <Star className="h-4 w-4 text-primary" fill="currentColor" />
                  </div>
                  <div>
                    <p className="font-medium">Antena 1</p>
                    <p className="text-xs text-muted-foreground">Adulto Contemporâneo</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-2 rounded-full">
                    <Star className="h-4 w-4 text-primary" fill="currentColor" />
                  </div>
                  <div>
                    <p className="font-medium">89 FM A Rádio Rock</p>
                    <p className="text-xs text-muted-foreground">Rock</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-2 rounded-full">
                    <Star className="h-4 w-4 text-primary" fill="currentColor" />
                  </div>
                  <div>
                    <p className="font-medium">CBN</p>
                    <p className="text-xs text-muted-foreground">Notícias</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-2 rounded-full">
                    <Star className="h-4 w-4 text-primary" fill="currentColor" />
                  </div>
                  <div>
                    <p className="font-medium">Kiss FM</p>
                    <p className="text-xs text-muted-foreground">Rock Clássico</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Acesse o Site Original</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Visite o site original para acessar todas as funcionalidades e opções de estações de rádio online.
                </p>
                <Button 
                  asChild
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <a 
                    href="https://seulink.digital/gXoiZ4kLb" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" /> Acessar Rádio Online
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        <Tabs defaultValue="por-pais" className="mb-12">
          <TabsList className="grid grid-cols-3 md:w-[600px] mx-auto">
            <TabsTrigger value="por-pais">Por País</TabsTrigger>
            <TabsTrigger value="por-genero">Por Gênero</TabsTrigger>
            <TabsTrigger value="favoritas">Favoritas</TabsTrigger>
          </TabsList>
          
          <TabsContent value="por-pais" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Rádios por País</CardTitle>
                <CardDescription>Explore estações de rádio de diferentes países</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                  <Button variant="outline" className="h-auto flex-col py-6 space-y-2">
                    <Globe className="h-8 w-8 text-primary" />
                    <span>Brasil</span>
                    <span className="text-xs text-muted-foreground">245 estações</span>
                  </Button>
                  
                  <Button variant="outline" className="h-auto flex-col py-6 space-y-2">
                    <Globe className="h-8 w-8 text-primary" />
                    <span>EUA</span>
                    <span className="text-xs text-muted-foreground">412 estações</span>
                  </Button>
                  
                  <Button variant="outline" className="h-auto flex-col py-6 space-y-2">
                    <Globe className="h-8 w-8 text-primary" />
                    <span>Portugal</span>
                    <span className="text-xs text-muted-foreground">86 estações</span>
                  </Button>
                  
                  <Button variant="outline" className="h-auto flex-col py-6 space-y-2">
                    <Globe className="h-8 w-8 text-primary" />
                    <span>Espanha</span>
                    <span className="text-xs text-muted-foreground">134 estações</span>
                  </Button>
                  
                  <Button variant="outline" className="h-auto flex-col py-6 space-y-2">
                    <Globe className="h-8 w-8 text-primary" />
                    <span>França</span>
                    <span className="text-xs text-muted-foreground">178 estações</span>
                  </Button>
                  
                  <Button variant="outline" className="h-auto flex-col py-6 space-y-2">
                    <Globe className="h-8 w-8 text-primary" />
                    <span>Inglaterra</span>
                    <span className="text-xs text-muted-foreground">203 estações</span>
                  </Button>
                  
                  <Button variant="outline" className="h-auto flex-col py-6 space-y-2">
                    <Globe className="h-8 w-8 text-primary" />
                    <span>Japão</span>
                    <span className="text-xs text-muted-foreground">92 estações</span>
                  </Button>
                  
                  <Button variant="outline" className="h-auto flex-col py-6 space-y-2">
                    <Globe className="h-8 w-8 text-primary" />
                    <span>Mais Países</span>
                    <span className="text-xs text-muted-foreground">1200+ estações</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="por-genero" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Rádios por Gênero</CardTitle>
                <CardDescription>Encontre estações de acordo com seu gosto musical</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                  <Button variant="outline" className="h-auto flex-col py-6 space-y-2">
                    <Music className="h-8 w-8 text-primary" />
                    <span>Pop</span>
                    <span className="text-xs text-muted-foreground">182 estações</span>
                  </Button>
                  
                  <Button variant="outline" className="h-auto flex-col py-6 space-y-2">
                    <Music className="h-8 w-8 text-primary" />
                    <span>Rock</span>
                    <span className="text-xs text-muted-foreground">156 estações</span>
                  </Button>
                  
                  <Button variant="outline" className="h-auto flex-col py-6 space-y-2">
                    <Music className="h-8 w-8 text-primary" />
                    <span>Sertanejo</span>
                    <span className="text-xs text-muted-foreground">87 estações</span>
                  </Button>
                  
                  <Button variant="outline" className="h-auto flex-col py-6 space-y-2">
                    <Music className="h-8 w-8 text-primary" />
                    <span>MPB</span>
                    <span className="text-xs text-muted-foreground">64 estações</span>
                  </Button>
                  
                  <Button variant="outline" className="h-auto flex-col py-6 space-y-2">
                    <Music className="h-8 w-8 text-primary" />
                    <span>Jazz</span>
                    <span className="text-xs text-muted-foreground">42 estações</span>
                  </Button>
                  
                  <Button variant="outline" className="h-auto flex-col py-6 space-y-2">
                    <Music className="h-8 w-8 text-primary" />
                    <span>Clássica</span>
                    <span className="text-xs text-muted-foreground">38 estações</span>
                  </Button>
                  
                  <Button variant="outline" className="h-auto flex-col py-6 space-y-2">
                    <Music className="h-8 w-8 text-primary" />
                    <span>Eletrônica</span>
                    <span className="text-xs text-muted-foreground">95 estações</span>
                  </Button>
                  
                  <Button variant="outline" className="h-auto flex-col py-6 space-y-2">
                    <Music className="h-8 w-8 text-primary" />
                    <span>Mais Gêneros</span>
                    <span className="text-xs text-muted-foreground">500+ estações</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="favoritas" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Minhas Rádios Favoritas</CardTitle>
                <CardDescription>Acesse rapidamente suas estações preferidas</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {radioStations.filter(station => station.isFavorite).length > 0 ? (
                    radioStations
                      .filter(station => station.isFavorite)
                      .map(station => (
                        <div 
                          key={station.id}
                          className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors"
                          onClick={() => handleStationSelect(station)}
                        >
                          <img 
                            src={station.image} 
                            alt={station.name}
                            className="w-16 h-16 rounded-md object-cover"
                          />
                          <div className="flex-1">
                            <h3 className="font-medium">{station.name}</h3>
                            <p className="text-sm text-muted-foreground">{station.genre}</p>
                            <p className="text-xs text-muted-foreground">{station.frequency} - {station.location}</p>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(station.id);
                            }}
                          >
                            <Heart className="h-5 w-5" fill="currentColor" />
                          </Button>
                        </div>
                      ))
                  ) : (
                    <div className="text-center py-12">
                      <Heart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Você ainda não tem estações favoritas</p>
                      <p className="text-sm text-muted-foreground mt-2">Clique no ícone de coração para adicionar estações aos seus favoritos</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="text-center">
          <Button 
            asChild 
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <a 
              href="https://seulink.digital/gXoiZ4kLb" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <ExternalLink className="mr-2 h-5 w-5" /> Acessar Todas as Rádios Online
            </a>
          </Button>
          <p className="mt-4 text-sm text-muted-foreground">
            Milhares de estações de rádio do Brasil e do mundo para ouvir gratuitamente.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RadioOnlinePage;